import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import CheckoutPayment from "../../components/shopping/CheckoutPayment";
import { ApiHook } from "../../hooks/apiHook";
import { ScaleLoader } from "react-spinners";
import { useSelector } from "react-redux";
import CurrencyConverter from "../../Currency/CurrencyConverter";
import { X } from "lucide-react";
import { Modal, Button } from "react-bootstrap";
import "bootstrap/dist/css/bootstrap.min.css";

const CheckoutModal = ({ product, isOpen, setIsOpen }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [quantity, setQuantity] = useState(0);

  const [paymentIntentId, setPaymentIntentId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [stripePublicKey, setStripePublicKey] = useState("");

  const paymentMethods = ApiHook.CallPaymentMethods("repurchase");
  const purchaseData = ApiHook.CallPurchaseData(product);


  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      paymentType: undefined,
      totalAmount: 0,
      product: null,
    },
  });

  const formValues = watch();

  const addCartMutation = ApiHook.CallAddToCart();
  const decrementCartMutation = ApiHook.CallDecrementCartItem();
  const removeCartMutation = ApiHook.CallRemoveCartItem();

  const currency = useSelector((state) => state.user?.selectedCurrency);
  const conversionFactor = useSelector((state) => state.user?.conversionFactor);

  let productData = purchaseData?.data?.productData;

  if (productData) {
    productData.currency = currency?.symbolLeft ? currency?.symbolLeft : "$";
  }

  const totalAmount = React.useMemo(() => {
    if (productData) {
      return productData.price * quantity + productData.fee * quantity;
    }
    return 0;
  }, [productData, quantity]);

  useEffect(() => {
    if (currentStep === 3) {
      const product = [
        {
          name: productData.name,
          quantity,
          totalAmount,
        },
      ];
      setValue("addressId", 1);
      setValue("totalAmount", totalAmount);
      setValue("product", product);
      setValue("isSingleProduct", true);
      setValue("packageId", productData.id);
    }
  }, [currentStep, totalAmount, setValue]);

  useEffect(() => {
    if (productData) {
      setQuantity(productData.cartQuantity);
    }
  }, [productData]);

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    } else {
      if (productData?.id) {
        handleRemoveCartItem(productData.id);
      }
      setIsOpen(false);
    }
  };

  const handleQuantityChange = (event, packageId) => {
    if (event.target.id === "plus") {
      addCartMutation.mutate(
        { packageId: packageId },
        {
          onSuccess: (res) => {
            if (res.status) {
              setQuantity((prev) => prev + 1);
            }
          },
        }
      );
    } else if (event.target.id === "minus") {
      decrementCartMutation.mutate(
        { packageId: packageId },
        {
          onSuccess: (res) => {
            if (res.status) {
              setQuantity((prev) => Math.max(1, prev - 1));
            }
          },
        }
      );
    }
  };

  const handleRemoveCartItem = (packageId) => {
    removeCartMutation.mutate(
      { packageId: packageId },
      {
        onSuccess: (res) => {
          if (res.status) {
            alert("Cleared");
          }
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <Modal
      className="modal-checkout"
      show={isOpen}
      onHide={() => setIsOpen(false)}
      size="xl"
      centered
    >
      <Modal.Body>
        <div className="modal-header">
          <h2>Checkout</h2>
          <button
            className="close-button"
            onClick={() => {
              handleRemoveCartItem(productData?.id);
              setIsOpen(false);
            }}
            title="Close"
          >
            <X style={{ marginBottom: ".7rem" }} size={20} />
          </button>
        </div>

        {/* Progress Steps */}
        <div className="progress-container">
          {[1, 2, 3].map((step) => (
            <div key={step} className="progress-step">
              <div
                className={`step-circle ${currentStep >= step ? "active" : ""}`}
              >
                {step}
              </div>
              <span className="step-label">Step {step}</span>
              {step < 3 && <div className="step-line"></div>}
            </div>
          ))}
        </div>

        <form className="checkout-form">
          {currentStep === 1 &&
            (productData ? (
              <div className="step-content">
                <h3>Select Quantity</h3>
                <div className="product-card">
                  <div className="product-info">
                    <div className="product-image">
                      {!productData.image ? (
                        <img src={productData.image} alt="Xion Image" />
                      ) : (
                        <div className="product-placeholder">XN</div>
                      )}
                    </div>
                    <div className="product-details">
                      <h4>{productData.name}</h4>
                      <p className="product-price">
                        {productData.currency}{" "}
                        {CurrencyConverter(productData.price, conversionFactor)}
                      </p>
                    </div>
                  </div>
                  <div className="quantity-controls">
                    <button
                      id="minus"
                      type="button"
                      onClick={(e) => handleQuantityChange(e, productData?.id)}
                      className="quantity-btn"
                    >
                      -
                    </button>
                    <span className="quantity-display">{quantity}</span>
                    <button
                      id="plus"
                      type="button"
                      onClick={(e) => handleQuantityChange(e, productData?.id)}
                      className="quantity-btn"
                    >
                      +
                    </button>
                  </div>
                </div>
                <div className="pricing-summary">
                  <div className="price-row">
                    <span>Subtotal:</span>
                    <span>
                      {productData.currency}{" "}
                      {CurrencyConverter(
                        productData.price * quantity,
                        conversionFactor
                      )}
                    </span>
                  </div>
                  <div className="price-row">
                    <span>Fee:</span>
                    <span>
                      {productData.currency}{" "}
                      {CurrencyConverter(
                        productData.fee * quantity,
                        conversionFactor
                      )}
                    </span>
                  </div>
                  <div className="price-row total">
                    <span>Total:</span>
                    <span>
                      {productData.currency}{" "}
                      {CurrencyConverter(totalAmount, conversionFactor)}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
                className="step-content"
              >
                <ScaleLoader color="#525252" />
              </div>
            ))}

          {currentStep === 2 && (
            <div className="step-content">
              <h3>Order Summary</h3>
              <div className="overview-section">
                <div className="order-details-card">
                  <div className="order-header">
                    <h4>Order Details</h4>
                  </div>

                  <div className="product-summary">
                    <div className="product-row">
                      <div className="product-info-summary">
                        <div className="product-image">
                          <div className="product-placeholder">XN</div>
                        </div>
                        <div className="product-details-summary">
                          <h5>{productData.name}</h5>
                          <p className="product-meta">Digital Asset • NFT</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="order-breakdown">
                    <div className="breakdown-row">
                      <span className="breakdown-label">Quantity</span>
                      <span className="breakdown-value">{quantity}</span>
                    </div>
                    <div className="breakdown-row">
                      <span className="breakdown-label">
                        Subtotal ({quantity} item{quantity > 1 ? "s" : ""})
                      </span>
                      <span className="breakdown-value">
                        {productData.currency}{" "}
                        {CurrencyConverter(
                          productData.price * quantity,
                          conversionFactor
                        )}
                      </span>
                    </div>
                    <div className="breakdown-row">
                      <span className="breakdown-label">
                        Processing Fee
                        <span className="fee-info">ⓘ</span>
                      </span>
                      <span className="breakdown-value">
                        {productData.currency}{" "}
                        {CurrencyConverter(
                          productData.fee * quantity,
                          conversionFactor
                        )}
                      </span>
                    </div>
                    <div className="breakdown-row total-row">
                      <span className="breakdown-label total-label">
                        Total Amount
                      </span>
                      <span className="breakdown-value total-value">
                        {productData.currency}{" "}
                        {CurrencyConverter(totalAmount, conversionFactor)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {currentStep === 3 && (
            <CheckoutPayment
              register={register}
              formValues={formValues}
              setValue={setValue}
              type={"purchase"}
              setIsOpen={setIsOpen}
              errors={errors}
              totalAmount={totalAmount}
              payments={paymentMethods?.data}
              paymentIntentId={paymentIntentId}
              setPaymentIntentId={setPaymentIntentId}
              clientSecret={clientSecret}
              setClientSecret={setClientSecret}
              stripePublicKey={stripePublicKey}
              setStripePublicKey={setStripePublicKey}
              purchaseType={"package"}
            />
          )}

          {currentStep < 3 && (
            <div className="modal-actions">
              <button type="button" className="back-button" onClick={prevStep}>
                {currentStep === 1 ? "Close" : "← Back"}
              </button>
              <button type="button" onClick={nextStep} className="continue-btn">
                Next
              </button>
            </div>
          )}
        </form>
      </Modal.Body>
    </Modal>
  );
};

export default CheckoutModal;
